import { AnyAction } from 'redux';
import {
  App,
  Gefahr,
  Leistung,
  LeistungEigenschaft,
  Produkt,
  Produktlinie,
  RsVorbelegungdaten,
} from '@ps20-frontend/frontend-models';
import { produktReducer } from '@ps20-frontend/reducers';
import cloneDeep from 'lodash/cloneDeep';
import { GlobalActions, TARIF_CHECKED, VorbelegungPayload } from '@ps20-frontend/shop-integration-redux';
import { Produktlinienname } from '@ps20/bff-models';
import { RsActions } from '../rsActions';
import { OptionaleLeistungChanged } from '../models/optinaleLeistungChanged';
import { ZusatzoptionenNameRSEnum } from '../models/zusatzoptionenRS.model';
import { isStrafschutzDisabled } from '../../app/shared/selectors/produkt.selector';

interface GefahrVorbelegung { produktName: Produktlinienname; gefahr: string; value: boolean }

interface ProduktWithVorbelegung extends Produkt {
  _gefahrenVorbelegung?: Array<GefahrVorbelegung>;
}

export function rsProduktReducer(sectionState: Produkt = null, action: AnyAction): Produkt {
  const newSectionState = produktReducer(sectionState, action, App.RS);
  switch (action.type) {
    case TARIF_CHECKED: {
      const clonedState = cloneDeep(newSectionState);
      deselectDisabledStrafrechtschutz(clonedState.produktlinien);
      return clonedState;
    }
    case RsActions.OPTIONALE_LEISTUNG_CHANGED:
      return handleOptionaleLeistungChanged(sectionState, newSectionState, action.payload);
    case RsActions.PRODUKTLINIEN_CHANGED: {
      const produktlinien: Produktlinie[] = action.payload;
      return { ...newSectionState, produktlinien };
    }
    case GlobalActions.VORBELEGUNG_FINISHED:
      return handleVorbelegungFinished(newSectionState, action.payload);
    default:
      return reapplyStoredVorbelegung(sectionState, newSectionState);
  }
}

function handleOptionaleLeistungChanged(
  sectionState: Produkt,
  newSectionState: Produkt,
  actionPayload: OptionaleLeistungChanged,
): Produkt {
  const clonedState: Produkt = cloneDeep(newSectionState);

  const produktlinie = clonedState.produktlinien.find(
    _produktlinie => _produktlinie.produktName === actionPayload.produktName,
  );
  if (produktlinie) {
    const optionaleLeistung = produktlinie.leistungen.find(
      leistung => leistung.id === actionPayload.eigenschaftId,
    );
    if (optionaleLeistung) {
      optionaleLeistung.eigenschaft = getLeistungEigenschaft(actionPayload.value);
    }
  }

  changeSelectionOfGefahr(clonedState.produktlinien, actionPayload);
  deselectDisabledStrafrechtschutz(clonedState.produktlinien);
  // Update stored vorbelegung to reflect manual user change
  const existingVorbelegung = (sectionState as ProduktWithVorbelegung)?._gefahrenVorbelegung;
  if (existingVorbelegung?.length > 0) {
    const updated = existingVorbelegung.map(entry =>
      entry.produktName === actionPayload.produktName && entry.gefahr === actionPayload.eigenschaftId
        ? { ...entry, value: actionPayload.value }
        : entry,
    );
    (clonedState as ProduktWithVorbelegung)._gefahrenVorbelegung = updated;
  }
  return clonedState;
}

function collectGefahrenVorbelegung(
  rsVorbelegungdaten: RsVorbelegungdaten,
): Array<GefahrVorbelegung> {
  const gefahrenVorbelegung: Array<GefahrVorbelegung> = [];
  if (rsVorbelegungdaten.komfortSelectedVerkehr != null) {
    gefahrenVorbelegung.push({ produktName: Produktlinienname.KOMFORT, gefahr: 'verkehr', value: rsVorbelegungdaten.komfortSelectedVerkehr });
  }
  if (rsVorbelegungdaten.premiumSelectedVerkehr != null) {
    gefahrenVorbelegung.push({ produktName: Produktlinienname.PREMIUM, gefahr: 'verkehr', value: rsVorbelegungdaten.premiumSelectedVerkehr });
  }
  if (rsVorbelegungdaten.komfortSelectedBeruf != null) {
    gefahrenVorbelegung.push({ produktName: Produktlinienname.KOMFORT, gefahr: 'beruf', value: rsVorbelegungdaten.komfortSelectedBeruf });
  }
  if (rsVorbelegungdaten.premiumSelectedBeruf != null) {
    gefahrenVorbelegung.push({ produktName: Produktlinienname.PREMIUM, gefahr: 'beruf', value: rsVorbelegungdaten.premiumSelectedBeruf });
  }
  return gefahrenVorbelegung;
}

function handleVorbelegungFinished(
  newSectionState: Produkt,
  actionPayload: VorbelegungPayload,
): Produkt {
  if (actionPayload.appId !== App.RS || !newSectionState?.produktlinien) {
    return newSectionState;
  }
  const rsVorbelegungdaten = actionPayload.vorhandeneVorbelegungdaten as RsVorbelegungdaten;
  if (!rsVorbelegungdaten) {
    return newSectionState;
  }
  const gefahrenVorbelegung = collectGefahrenVorbelegung(rsVorbelegungdaten);
  if (gefahrenVorbelegung.length === 0) {
    return newSectionState;
  }
  const clonedState: Produkt = cloneDeep(newSectionState);
  applyGefahrenVorbelegung(clonedState, gefahrenVorbelegung);
  // Store for re-application after PREISE_STRECKE_ERMITTELT overwrites produktlinien
  (clonedState as ProduktWithVorbelegung)._gefahrenVorbelegung = gefahrenVorbelegung;
  return clonedState;
}

function reapplyStoredVorbelegung(sectionState: Produkt, newSectionState: Produkt): Produkt {
  // Re-apply gefahren vorbelegung after PREISE_STRECKE_ERMITTELT overwrites produktlinien
  const storedVorbelegung = (sectionState as ProduktWithVorbelegung)?._gefahrenVorbelegung;
  if (storedVorbelegung?.length > 0 && newSectionState?.produktlinien && newSectionState !== sectionState) {
    const clonedState: Produkt = cloneDeep(newSectionState);
    applyGefahrenVorbelegung(clonedState, storedVorbelegung);
    (clonedState as ProduktWithVorbelegung)._gefahrenVorbelegung = storedVorbelegung;
    return clonedState;
  }
  return newSectionState;
}

function applyGefahrenVorbelegung(state: Produkt, gefahrenVorbelegung: Array<GefahrVorbelegung>): void {
  gefahrenVorbelegung.forEach(({ produktName, gefahr, value }) => {
    const produktlinie = state.produktlinien.find(pl => pl.produktName === produktName);
    const gefahrObj = produktlinie?.gefahren?.find(g => g.id === gefahr);
    if (gefahrObj?.isSelectable) {
      gefahrObj.isSelected = value;
      changeLeistungenBeiGefahr(produktlinie.leistungen, gefahrObj);
    }
  });
  deselectDisabledStrafrechtschutz(state.produktlinien);
}

function changeSelectionOfGefahr(
  produktlinien: Produktlinie[],
  actionPayload: OptionaleLeistungChanged,
): void {
  const produktlinie = produktlinien.find(
    _produktlinie => _produktlinie.produktName === actionPayload.produktName,
  );
  const waehlbareGefahren: Array<string> = ['verkehr', 'beruf'];
  if (produktlinie) {
    const gefahr = produktlinie.gefahren.find(
      _gefahr => _gefahr.id === actionPayload.eigenschaftId,
    );
    if (gefahr) {
      gefahr.isSelected = actionPayload.value;
      if (waehlbareGefahren.includes(gefahr.id)) {
        changeLeistungenBeiGefahr(produktlinie.leistungen, gefahr);
      }
    }
  }
}

function getLeistungEigenschaft(toggle: boolean): LeistungEigenschaft {
  return toggle ? LeistungEigenschaft.INKLUSIVE : LeistungEigenschaft.EXKLUSIVE;
}

function deselectDisabledStrafrechtschutz(produktlinien: Produktlinie[]): void {
  produktlinien.forEach(produktlinie => {
    const strafrechtschutzZusatzoption = produktlinie.zusatzoptionen.find(
      zusatzoption => zusatzoption.name === ZusatzoptionenNameRSEnum.STRAFRECHTSSCHUTZ,
    );
    if (isStrafschutzDisabled(produktlinie) && strafrechtschutzZusatzoption) {
      strafrechtschutzZusatzoption.isSelected = false;
    }
  });
}

function changeLeistungenBeiGefahr(leistungen: Leistung[], gefahr: Gefahr): void {
  leistungen
    .filter(
      lst =>
        lst.id === gefahr.id || lst.kategorie?.toLowerCase().includes(gefahr.id),
    )
    .forEach(lst => (lst.abgewaehlt = !gefahr.isSelected));
}
